import { useEffect, useRef, useState, type RefObject } from 'react';
import { Button } from '@/components/ui';

/**
 * A just-in-time spotlight: scrolls a target element into view, slightly darkens the rest of the
 * page, and rings the target with a callout pointing to it. The overlay is non-blocking (the
 * darkening is a box-shadow, container is pointer-events-none) so the real control underneath
 * stays clickable — guidance, not a modal trap. Dismiss via "Got it" or Escape.
 *
 * Target the element either with a ref (`targetRef`) or a CSS selector (`targetSelector`). The
 * selector form is re-resolved every frame, so it works for controls that are deeply nested or
 * mount slightly after the page (e.g. once data loads).
 */
export function Coachmark({
  active,
  targetRef,
  targetSelector,
  message,
  onDismiss,
  primaryLabel = 'Got it',
  placement = 'auto',
}: {
  active: boolean;
  targetRef?: RefObject<HTMLElement | null>;
  targetSelector?: string;
  message: string;
  onDismiss: () => void;
  /** Label for the callout's primary button. Defaults to "Got it"; use "Next"/"Done" for steppers. */
  primaryLabel?: string;
  /**
   * Where the callout sits relative to the target. 'auto' (default) places it below, flipping above
   * if it would overflow. 'right' places it to the right, vertically centered — for tall, narrow,
   * left-edge targets like the nav rail, where below/above would overhang.
   */
  placement?: 'auto' | 'right';
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const scrolledRef = useRef(false);

  useEffect(() => {
    if (!active) {
      setRect(null);
      scrolledRef.current = false;
      return;
    }

    // Track the target's position every frame so the spotlight stays aligned through the smooth
    // scroll (the page often scrolls inside a container, so window scroll events don't cover it),
    // and so a late-mounting target gets picked up as soon as it appears.
    let raf = 0;
    const tick = () => {
      const el = targetRef?.current ?? (targetSelector ? document.querySelector<HTMLElement>(targetSelector) : null);
      const r = el?.getBoundingClientRect();
      // Only spotlight a target that's actually laid out and visible. Otherwise clear any prior
      // rect so we never leave a stray square when the target moves, hides, or unmounts (e.g. a
      // layout shift from opening the campaign dropdown).
      if (el && r && r.width > 0 && r.height > 0) {
        setRect(r);
        if (!scrolledRef.current) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
          scrolledRef.current = true;
        }
      } else {
        setRect((prev) => (prev === null ? prev : null));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('keydown', onKey);
    };
  }, [active, targetRef, targetSelector, onDismiss]);

  if (!active || !rect) return null;

  const pad = 8;
  const margin = 12;
  const calloutWidth = 288; // w-72
  const estCalloutHeight = 150; // conservative — used only to decide whether to flip above
  // Side-aware horizontal anchoring: targets on the right half of the screen anchor the callout
  // to their RIGHT edge (extends into the page); left-side targets anchor to their left. Then
  // clamp so the callout stays fully on-screen. Vertical: flip above if below would overflow.
  const clampLeft = (v: number) => Math.max(margin, Math.min(v, window.innerWidth - calloutWidth - margin));
  const clampTop = (v: number) => Math.max(margin, Math.min(v, window.innerHeight - estCalloutHeight - margin));

  let calloutLeft: number;
  let calloutTop: number;
  if (placement === 'right') {
    // To the right of the target, vertically centered on it — tidy for the left nav rail.
    calloutLeft = clampLeft(rect.right + margin);
    calloutTop = clampTop(rect.top + rect.height / 2 - estCalloutHeight / 2);
  } else {
    const targetCenterX = rect.left + rect.width / 2;
    const desiredLeft = targetCenterX > window.innerWidth / 2 ? rect.right - calloutWidth : rect.left;
    calloutLeft = clampLeft(desiredLeft);
    calloutTop =
      rect.bottom + margin + estCalloutHeight > window.innerHeight - margin
        ? Math.max(margin, rect.top - estCalloutHeight - margin)
        : rect.bottom + margin;
  }

  return (
    // pointer-events-none so clicks pass through to the (still-lit) control beneath.
    <div className="pointer-events-none fixed inset-0 z-[100]">
      {/* Spotlight: dim the screen and leave a transparent rounded "hole" over the target. The
          rect is cleared whenever the target is missing/zero-size (see tick), so the dim never
          sticks around as a stray box. */}
      <div
        className="absolute rounded-xl transition-[top,left,width,height] duration-150"
        style={{
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
          boxShadow: '0 0 0 9999px rgba(0,0,0,0.45)',
        }}
      />
      {/* Callout pointing at the target. */}
      <div
        className="pointer-events-auto absolute w-72 rounded-lg border bg-card p-3 shadow-lg transition-[top,left] duration-200"
        style={{ top: calloutTop, left: calloutLeft }}
      >
        <p className="text-sm text-foreground">{message}</p>
        <div className="mt-2 flex justify-end">
          <Button size="sm" onClick={onDismiss}>
            {primaryLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}
